/**
 * Position calculation utilities for comment bubbles
 */

import { Direction } from './commentTypes';

/**
 * Calculate tail direction (points toward center of base element)
 */
export const calculateTailDirection = (
  bubbleRef: React.RefObject<HTMLElement | null>,
  baseElementRef: React.RefObject<HTMLElement | null>,
): Direction => {
  if (!bubbleRef.current || !baseElementRef?.current) return 'bottom';

  const bubbleRect = bubbleRef.current.getBoundingClientRect();
  const baseRect = baseElementRef.current.getBoundingClientRect();

  // Calculate centers
  const bubbleCenterX = bubbleRect.left + bubbleRect.width / 2;
  const bubbleCenterY = bubbleRect.top + bubbleRect.height / 2;
  const baseCenterX = baseRect.left + baseRect.width / 2;
  const baseCenterY = baseRect.top + baseRect.height / 2;

  // Calculate direction vector from bubble to base element center
  const deltaX = baseCenterX - bubbleCenterX;
  const deltaY = baseCenterY - bubbleCenterY;

  // Determine primary direction based on which delta is larger
  if (Math.abs(deltaX) > Math.abs(deltaY)) {
    // Horizontal direction is primary
    return deltaX > 0 ? 'right' : 'left';
  } else {
    // Vertical direction is primary
    return deltaY > 0 ? 'bottom' : 'top';
  }
};

/**
 * Calculate tooltip position based on bubble location in viewport
 */
export const calculateTooltipPosition = (
  bubbleRef: React.RefObject<HTMLElement | null>,
): Direction => {
  if (!bubbleRef.current) return 'top';

  const rect = bubbleRef.current.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  const centerX = rect.left + rect.width / 2;
  const centerY = rect.top + rect.height / 2;

  const isLeftHalf = centerX < viewportWidth / 2;
  const isTopHalf = centerY < viewportHeight / 2;

  // Prioritize horizontal positioning if there's enough space
  const spaceLeft = rect.left;
  const spaceRight = viewportWidth - rect.right;
  const spaceTop = rect.top;
  const spaceBottom = viewportHeight - rect.bottom;

  const minSpace = 300; // Minimum space needed for tooltip

  if (spaceRight > minSpace && isLeftHalf) {
    return 'right';
  } else if (spaceLeft > minSpace && !isLeftHalf) {
    return 'left';
  } else if (spaceTop > minSpace && !isTopHalf) {
    return 'top';
  } else if (spaceBottom > minSpace && isTopHalf) {
    return 'bottom';
  } else {
    // Fallback to the side with most space
    const maxSpace = Math.max(spaceLeft, spaceRight, spaceTop, spaceBottom);
    if (maxSpace === spaceRight) return 'right';
    if (maxSpace === spaceLeft) return 'left';
    if (maxSpace === spaceTop) return 'top';
    return 'bottom';
  }
};

/**
 * Generate random position within container bounds
 */
export const generateRandomPosition = (
  containerBounds: DOMRect,
  padding: number = 20,
): { x: number; y: number } => {
  const maxX = containerBounds.width - 200 - padding; // Account for comment bubble width
  const maxY = containerBounds.height - 100 - padding; // Account for comment bubble height

  const randomX = Math.max(padding, Math.random() * maxX);
  const randomY = Math.max(padding, Math.random() * maxY);

  return { x: randomX, y: randomY };
};

/**
 * Get comment position with fallback logic
 */
export const getCommentPosition = (
  comment: { id: string; position?: Record<string, any> | null },
  commentPositions: Record<string, { x: number; y: number }>,
): { x: number; y: number } => {
  // Check if we have a locally stored position for this comment
  if (commentPositions[comment.id]) {
    return commentPositions[comment.id];
  }

  // If comment has a saved position, use it
  if (
    comment.position &&
    typeof comment.position.x === 'number' &&
    typeof comment.position.y === 'number'
  ) {
    return { x: comment.position.x, y: comment.position.y };
  }

  // Fallback position (will be replaced by random positioning logic)
  return { x: 20, y: 20 };
};
